import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
} from "react";

import { useMutation } from "@tanstack/react-query";
import { API_URL } from "../utilities";
import { getUser } from "../api/auth";
import { AuthContextType, IUser } from "../interface/IUser";

function AuthProvider(props: React.PropsWithChildren<unknown>) {
  const [user, setUser] = useState<IUser>();

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async function () {
      try {
        // بررسی وجود token در localStorage
        const token = localStorage.getItem("Token");
        if (!token) {
          console.log("🔐 هیچ token ای یافت نشد، کاربر logout است");
          setLoading(false);
          return;
        }

        console.log("🔐 token یافت شد، بررسی اعتبار...");
        const result = await getUser();

        if (result.isOk && result.data) {
          console.log("✅ کاربر authenticate شد:", result.data.userName);
          setUser(result.data);
        } else {
          console.log("❌ token نامعتبر، پاک کردن authentication data");
          // پاک کردن token های نامعتبر
          localStorage.removeItem("Token");
          localStorage.removeItem("Email");
          localStorage.removeItem("UserName");
          localStorage.removeItem("UserId");
          setUser(undefined);
        }
      } catch (error) {
        console.error("❌ خطا در بررسی authentication:", error);
        // در صورت خطا، کاربر را logout نکن، شاید مشکل موقتی باشد
        const token = localStorage.getItem("Token");
        if (token) {
          // اگر token وجود دارد، کاربر را با داده‌های localStorage authenticate کن
          const userData = {
            token,
            email: localStorage.getItem("Email") || "",
            userName: localStorage.getItem("UserName") || "",
            id: localStorage.getItem("UserId") || "",
          };
          console.log("⚠️ استفاده از داده‌های localStorage به دلیل خطا در API");
          setUser(userData);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const signIn = useCallback(
    async (email: string, password: string, mutateAsync: any) => {
      try {
        const result = await mutateAsync({
          UserName: email,
          Password: password,
        });
        console.log("🚀 ~ result:", result);

        if (result.status) {
          localStorage.setItem("Email", result?.data?.email);
          localStorage.setItem("UserName", result?.data?.userName);
          localStorage.setItem("Token", result?.data?.token);
          localStorage.setItem("UserId", result?.data?.id);
          setUser(result?.data);
        }
        return {
          isOk: result?.status,
          data: result?.data,
          message: result?.status
            ? "به پورتال تکنو حساب خوش آمدید"
            : "نام کاربری یا گذرواژه اشتباه است",
        };
      } catch (error) {
        console.error("Error during sign in:", error);
        return {
          isOk: false,
          data: null,
          message:
            "خطا در اتصال به سرور. لطفاً اتصال اینترنت خود را بررسی کنید.",
        };
      }
    },
    []
  );

  const createAccount = useCallback(
    async (
      userName: string,
      email: string,
      password: string,
      mutateAsync: any
    ) => {
      try {
        // Send request
        const result = await mutateAsync({
          UserName: userName,
          Email: email,
          Password: password,
        });
        if (result?.status) {
          localStorage.setItem("Email", result?.data?.email);
          localStorage.setItem("UserName", result?.data?.userName);
          localStorage.setItem("Token", result?.data?.token);
          localStorage.setItem("UserId", result?.data?.id);
          setUser(result.data);
        }
        return result;
      } catch (error) {
        console.error("Error during account creation:", error);
        return {
          status: false,
          message:
            "خطا در اتصال به سرور. لطفاً اتصال اینترنت خود را بررسی کنید.",
        };
      }
    },
    []
  );

  const signOut = useCallback(() => {
    localStorage.removeItem("Email");
    localStorage.removeItem("UserName");
    localStorage.removeItem("Token");
    localStorage.removeItem("UserId");
    setUser(undefined);
  }, []);
  return (
    <AuthContext.Provider
      value={{ user, signIn, signOut, loading, createAccount }}
      {...props}
    />
  );
}

const AuthContext = createContext<AuthContextType>({
  loading: false,
} as AuthContextType);
const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
